import { useCallback, useEffect, useState } from 'react';
import { authApi } from '@/api/auth.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import type { MfaSetupResponse } from '@/types/auth';

type MfaSettingsPanelProps = {
  /** Initial MFA state from session; refreshed from API on mount. */
  initialEnabled?: boolean;
  onStatusChange?: (enabled: boolean) => void;
  className?: string;
};

export function MfaSettingsPanel({
  initialEnabled = false,
  onStatusChange,
  className,
}: MfaSettingsPanelProps) {
  const [enabled, setEnabled] = useState(initialEnabled);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState<MfaSetupResponse | null>(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refreshStatus = useCallback(async () => {
    setLoading(true);
    try {
      const status = await authApi.mfaStatus();
      setEnabled(status.enabled);
      onStatusChange?.(status.enabled);
    } catch (err) {
      showApiError(err, 'Could not load MFA status');
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    setEnabled(initialEnabled);
  }, [initialEnabled]);

  const beginSetup = async () => {
    setSubmitting(true);
    try {
      const data = await authApi.mfaSetup();
      setSetup(data);
      setConfirmCode('');
      notify.info('Scan the QR code with your authenticator app');
    } catch (err) {
      showApiError(err, 'Could not start MFA setup');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSetup = async () => {
    if (!setup || confirmCode.length < 6) return;
    setSubmitting(true);
    try {
      await authApi.mfaConfirm({ secret: setup.secret, totpCode: confirmCode });
      setSetup(null);
      setConfirmCode('');
      setEnabled(true);
      onStatusChange?.(true);
      notify.success('Two-factor authentication enabled');
    } catch (err) {
      showApiError(err, 'Invalid authenticator code');
    } finally {
      setSubmitting(false);
    }
  };

  const disableMfa = async () => {
    if (disableCode.length < 6) return;
    setSubmitting(true);
    try {
      await authApi.mfaDisable({ totpCode: disableCode });
      setDisableCode('');
      setEnabled(false);
      onStatusChange?.(false);
      notify.success('Two-factor authentication disabled');
    } catch (err) {
      showApiError(err, 'Invalid authenticator code');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={className}>
        <p className="text-sm text-slate-500">Loading security settings…</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Two-factor authentication</h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Protect your account with a 6-digit code from Google Authenticator, Microsoft Authenticator, or similar apps.
          </p>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-medium ${
            enabled
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
          }`}
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      {enabled ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            You will be asked for an authenticator code each time you sign in.
          </p>
          <Input
            label="Authenticator code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter code to disable MFA"
          />
          <Button
            variant="secondary"
            loading={submitting}
            disabled={disableCode.length < 6}
            onClick={() => void disableMfa()}
          >
            Disable two-factor authentication
          </Button>
        </div>
      ) : setup ? (
        <div className="mt-4 space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <img
              src={setup.qrCodeDataUri}
              alt="MFA QR code"
              className="h-40 w-40 rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-700"
            />
            <div className="min-w-0 flex-1 text-sm text-slate-600 dark:text-slate-300">
              <p className="font-medium text-slate-900 dark:text-slate-100">Setup steps</p>
              <ol className="mt-2 list-decimal space-y-1 pl-4">
                <li>Open your authenticator app.</li>
                <li>Scan the QR code or enter the secret manually.</li>
                <li>Enter the 6-digit code below to confirm.</li>
              </ol>
              <p className="mt-3 break-all rounded-md bg-slate-50 px-2 py-1 font-mono text-xs dark:bg-slate-800">
                {setup.secret}
              </p>
            </div>
          </div>
          <Input
            label="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={confirmCode}
            onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <div className="flex flex-wrap gap-2">
            <Button loading={submitting} disabled={confirmCode.length < 6} onClick={() => void confirmSetup()}>
              Confirm and enable
            </Button>
            <Button
              variant="ghost"
              disabled={submitting}
              onClick={() => {
                setSetup(null);
                setConfirmCode('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <Button loading={submitting} onClick={() => void beginSetup()}>
            Enable two-factor authentication
          </Button>
        </div>
      )}
    </div>
  );
}
